import { SETTINGS_PERMISSIONS_MATRIX_COLUMNS } from "../constants/columns.ts"
import type { ActionRecord, ColumnKey, SectionRecord } from "../types.ts"
import type {
  PermissionDisplayMap,
  PermissionFeatures
} from "../types/permissions.types.ts"
import { getColKey, getDisplayEntry, getSectionTitle } from "./utils.ts"

export type MatrixColumn = (typeof SETTINGS_PERMISSIONS_MATRIX_COLUMNS)[number]

export interface PermissionRef {
  sectionKey: string
  subKey: string
  actionKey: string
  label: string
}

export interface MatrixRow {
  subKey: string
  label: string
  cells: Partial<Record<ColumnKey, string>>
  extras: PermissionRef[]
}

export type AdditionalGroupKey =
  | "approvals"
  | "team"
  | "financial"
  | "lifecycle"
  | "advanced"

export interface AdditionalGroup {
  key: AdditionalGroupKey
  items: PermissionRef[]
}

export interface SectionLayout {
  key: string
  title: string
  visibility: PermissionRef[]
  columns: readonly MatrixColumn[]
  rows: MatrixRow[]
  // Rights of a section with no create/view/edit/delete grid at all
  // (e-signature, communication), listed as plain toggles.
  actions: PermissionRef[]
  additional: AdditionalGroup[]
}

const ADDITIONAL_GROUPS: ReadonlyArray<{
  key: AdditionalGroupKey
  codes: readonly string[]
}> = [
  {
    key: "approvals",
    codes: [
      "project_approval",
      "project_budget_approval",
      "document_review",
      "lead_proposal_approval",
      "lead_contract_approval"
    ]
  },
  {
    key: "team",
    codes: [
      "project_assignable_as_pm",
      "project_assign_pm",
      "lead_assignable_as_sales",
      "lead_assign_sales_manager",
      "calendar_assign_to_others",
      "calendar_assignable_as_participant",
      "customer_invite",
      "subcontractor_invite",
      "team_invite_user",
      "team_modify_permissions",
      "team_admin_notifications",
      "team_manage_account_deletion"
    ]
  },
  {
    key: "financial",
    codes: [
      "project_snapshot_sales",
      "project_snapshot_commissions",
      "team_performance_view",
      "global_map_analytics_view"
    ]
  },
  {
    key: "lifecycle",
    codes: [
      "project_change_status",
      "project_close",
      "project_archive",
      "project_view_archive",
      "lead_view_archive"
    ]
  },
  // Anything the server adds that no group names lands here, so a new right is
  // never left without a control.
  {
    key: "advanced",
    codes: [
      "project_activity_log",
      "lead_activity_log",
      "project_view_exact_location",
      "lead_view_exact_location"
    ]
  }
]

const FEATURES_KEY = "features"

export const isVisibilityKey = (actionKey: string) =>
  actionKey.endsWith("_view_all")

const humanize = (key: string) => {
  const text = key.replace(/_/g, " ")
  return text.charAt(0).toUpperCase() + text.slice(1)
}

export const getPermissionLabel = (
  actionKey: string,
  displayMap?: PermissionDisplayMap | null,
  subKey?: string
) => {
  const name = getDisplayEntry(displayMap, actionKey)?.name
  if (name) return name
  const stripped =
    subKey && actionKey.startsWith(`${subKey}_`)
      ? actionKey.slice(subKey.length + 1)
      : actionKey
  return humanize(stripped)
}

export const getRowLabel = (
  subKey: string,
  displayMap?: PermissionDisplayMap | null
) => getDisplayEntry(displayMap, subKey)?.name ?? humanize(subKey)

export const getSectionLabel = (
  sectionKey: string,
  displayMap?: PermissionDisplayMap | null
) => {
  const title = getSectionTitle(
    sectionKey as keyof PermissionFeatures,
    displayMap
  )
  return title.charAt(0).toUpperCase() + title.slice(1)
}

const groupAdditional = (refs: PermissionRef[]): AdditionalGroup[] => {
  const byGroup = new Map<AdditionalGroupKey, PermissionRef[]>()
  refs.forEach((ref) => {
    const group =
      ADDITIONAL_GROUPS.find((g) => g.codes.includes(ref.actionKey))?.key ??
      "advanced"
    byGroup.set(group, [...(byGroup.get(group) ?? []), ref])
  })
  return ADDITIONAL_GROUPS.filter((g) => byGroup.has(g.key)).map((g) => ({
    key: g.key,
    items: byGroup.get(g.key) ?? []
  }))
}

export function getSectionLayout(
  sectionKey: string,
  section: SectionRecord,
  displayMap?: PermissionDisplayMap | null
): SectionLayout {
  const visibility: PermissionRef[] = []
  const featureRefs: PermissionRef[] = []
  const rows: MatrixRow[] = []
  const present = new Set<ColumnKey>()

  const toRef = (subKey: string, actionKey: string): PermissionRef => ({
    sectionKey,
    subKey,
    actionKey,
    label: getPermissionLabel(actionKey, displayMap, subKey)
  })

  Object.entries(section).forEach(([subKey, actions]) => {
    const actionKeys = Object.keys(actions as ActionRecord)

    if (subKey === FEATURES_KEY) {
      actionKeys.forEach((actionKey) =>
        (isVisibilityKey(actionKey) ? visibility : featureRefs).push(
          toRef(subKey, actionKey)
        )
      )
      return
    }

    const row: MatrixRow = {
      subKey,
      label: getRowLabel(subKey, displayMap),
      cells: {},
      extras: []
    }
    actionKeys.forEach((actionKey) => {
      const col = getColKey(actionKey, actions as ActionRecord)
      if (col) {
        row.cells[col] = actionKey
        present.add(col)
      } else if (isVisibilityKey(actionKey)) {
        visibility.push(toRef(subKey, actionKey))
      } else {
        row.extras.push(toRef(subKey, actionKey))
      }
    })
    rows.push(row)
  })

  const hasGrid = present.size > 0
  const columns = hasGrid
    ? SETTINGS_PERMISSIONS_MATRIX_COLUMNS.filter(
        (col) => col.key !== "full_access" || present.has("full_access")
      )
    : []

  return {
    key: sectionKey,
    title: getSectionLabel(sectionKey, displayMap),
    visibility,
    columns,
    rows: hasGrid ? rows : [],
    actions: hasGrid ? [] : rows.flatMap((row) => row.extras),
    additional: groupAdditional(featureRefs)
  }
}

export const getRowDisclosureId = (sectionKey: string, subKey: string) =>
  `row:${sectionKey}:${subKey}`

export const getGroupDisclosureId = (
  sectionKey: string,
  groupKey: AdditionalGroupKey
) => `group:${sectionKey}:${groupKey}`

export interface SearchResult extends PermissionRef {
  sectionTitle: string
  // The disclosure that has to be open for the control to be on screen.
  disclosureId?: string
}

export function buildSearchIndex(
  layouts: SectionLayout[],
  columnLabel: (col: ColumnKey) => string
): SearchResult[] {
  return layouts.flatMap((layout) => {
    const base = { sectionTitle: layout.title }

    return [
      ...layout.visibility.map((ref) => ({ ...ref, ...base })),
      ...layout.rows.flatMap((row) => [
        ...Object.entries(row.cells).map(([col, actionKey]) => ({
          ...base,
          sectionKey: layout.key,
          subKey: row.subKey,
          actionKey: actionKey as string,
          label: `${row.label} — ${columnLabel(col as ColumnKey)}`
        })),
        ...row.extras.map((ref) => ({
          ...ref,
          ...base,
          label: `${row.label} — ${ref.label}`,
          disclosureId: getRowDisclosureId(layout.key, row.subKey)
        }))
      ]),
      ...layout.actions.map((ref) => ({ ...ref, ...base })),
      ...layout.additional.flatMap((group) =>
        group.items.map((ref) => ({
          ...ref,
          ...base,
          disclosureId: getGroupDisclosureId(layout.key, group.key)
        }))
      )
    ]
  })
}

export function searchPermissions(
  index: SearchResult[],
  query: string
): SearchResult[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (terms.length === 0) return []
  return index.filter((result) => {
    const haystack =
      `${result.sectionTitle} ${result.label} ${result.actionKey}`.toLowerCase()
    return terms.every((term) => haystack.includes(term))
  })
}

export const getPermissionDomId = (actionKey: string) =>
  `permission-control-${actionKey}`
